import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, MapPin, Phone } from "lucide-react";
import { useApp, scrollToSection } from "../store";
import { useMediaItems } from "./SectionMedia";

const HERO_IMG =
  "https://images.unsplash.com/photo-1788626340246-5a9c8294a6b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxmcmVuY2glMjBwb2x5bmVzaWElMjBsYWdvb24lMjB0YWhpdGklMjBhZXJpYWwlMjBvY2VhbnxlbnwwfHx8fDE3ODkxNzgzMTZ8MA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const { c, t, lang } = useApp();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 48]);

  const heroSlides = useMediaItems()
    .filter((m) => (m.section || "realisations") === "hero" && !/\.(mp4|webm|mov)(\?|$)/i.test(m.url))
    .map((m) => m.url);
  const slides = heroSlides.length > 0 ? heroSlides : [c("hero_image") || HERO_IMG];
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return;
    const id = setInterval(() => setSlide((s) => (s + 1) % slides.length), 5000);
    return () => clearInterval(id);
  }, [slides.length]);

  const lines = (c("hero_title") || "").split("\n").filter(Boolean);

  return (
    <section id="hero" ref={ref} className="relative min-h-screen flex flex-col overflow-hidden grain text-white" style={{ background: "var(--sec-hero-bg, #0A1428)" }} data-testid="hero-section">
      <div className="relative z-10 flex-1 max-w-7xl mx-auto w-full px-5 sm:px-8 pt-36 pb-14 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="inline-flex items-center gap-3 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[var(--accent)] animate-pulse" />
            <span data-testid="hero-badge" className="font-mono2 text-xs sm:text-sm uppercase tracking-[0.3em] text-[var(--accent-soft)]">
              {c("hero_badge")}
            </span>
          </motion.div>

          <h1
            data-testid="hero-title"
            key={lang}
            className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight"
          >
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === lines.length - 1 ? "text-[var(--accent-soft)] italic" : "text-white"}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.45 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed"
          >
            {c("hero_subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-contact-button"
              onClick={() => scrollToSection("#contact")}
              className="group inline-flex items-center gap-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-300"
            >
              {t("cta_start")}
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-services-button"
              onClick={() => scrollToSection("#expertises")}
              className="inline-flex items-center gap-2 border border-[var(--accent-soft)]/25 hover:border-[var(--accent-soft)] hover:text-[var(--accent-soft)] text-white/80 font-medium px-7 py-3.5 rounded-full transition-colors duration-300"
            >
              {t("hero_cta_secondary")}
            </button>
          </motion.div>
        </div>

        <motion.div
          style={{ y: imgY }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="absolute -top-3 -right-3 w-full h-full border border-[var(--accent)]/50 pointer-events-none" />
          <div className="relative w-full aspect-video overflow-hidden bg-[#101E3D]">
            <AnimatePresence mode="sync">
              <motion.img
                key={slides[slide]}
                src={slides[slide]}
                alt="Polynésie Gestion et Projet"
                data-testid="hero-image"
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.2, ease: "easeOut" }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>
          </div>
          {slides.length > 1 && (
            <div data-testid="hero-slideshow-dots" className="absolute bottom-4 right-4 flex gap-1.5 z-10">
              {slides.map((_, i) => (
                <button
                  key={i}
                  data-testid={`hero-slide-dot-${i}`}
                  onClick={() => setSlide(i)}
                  aria-label={`Photo ${i + 1}`}
                  className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                    i === slide ? "bg-[var(--accent-soft)]" : "bg-[var(--accent-soft)]/25 hover:bg-[var(--accent-soft)]/50"
                  }`}
                />
              ))}
            </div>
          )}
          <span className="absolute bottom-4 left-4 bg-[#12254D]/95 backdrop-blur px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/80 border border-[var(--accent-soft)]/10">
            Papeete — Tahiti
          </span>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pb-8 flex items-end justify-between gap-6"
      >
        <div className="hidden sm:flex items-center gap-8 font-mono2 text-[11px] uppercase tracking-[0.25em] text-white/50">
          <span className="inline-flex items-center gap-2">
            <MapPin size={13} className="text-[var(--accent-soft)]" /> {c("contact_address").split("—")[0]}
          </span>
          <span className="inline-flex items-center gap-2">
            <Phone size={13} className="text-[var(--accent-soft)]" /> {c("contact_phone")}
          </span>
        </div>
        <button
          data-testid="hero-scroll-indicator"
          onClick={() => scrollToSection("#cabinet")}
          className="flex items-center gap-3 font-mono2 text-[11px] uppercase tracking-[0.3em] text-white/50 hover:text-[var(--accent-soft)] transition-colors duration-300"
        >
          {t("hero_scroll")}
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
            <ArrowDown size={14} />
          </motion.span>
        </button>
      </motion.div>
    </section>
  );
};
